import { useMemo, useState, useEffect } from 'react';
import { getIconComponent } from '../components/prd-workflow/foundation/iconUtils';
import sectionColorService from '../services/sectionColorService';
import { getTaskStatus, TASK_STATUS } from '../utils/taskStatusIndicator';
import conversationService from '../services/conversationService';
import { normalizeSectionName } from '../utils/sectionColors';

/**
 * Fallback task data for when backend data isn't available
 */
const FALLBACK_TILES_DATA = [
  // Core tasks
  {
    id: 'chat-prd',
    title: 'Project Info',
    icon: 'ChatIcon',
    description: 'Chat with your PRD to get insights and clarifications',
    status: 'available',
    step: -1,
    sectionName: 'Core'
  },
  {
    id: 'extract-prd',
    title: 'Extract PRD',
    icon: 'DescriptionIcon',
    description: 'Extract PRD content from conversations and documents',
    status: 'pending',
    step: 0,
    sectionName: 'Core'
  },
  
  // Field and Validation tasks
  {
    id: 'field-validations',
    title: 'Field Validations',
    icon: 'ContentPasteIcon',
    description: 'Define field validation rules for all inputs',
    status: 'pending',
    step: 1,
    sectionName: 'Validation'
  },
  
  // Technical tasks
  {
    id: 'tech-stack',
    title: 'Technology Stack',
    icon: 'DevicesIcon',
    description: 'Define the technology stack for the project',
    status: 'pending',
    step: 2,
    sectionName: 'Technical'
  },
  {
    id: 'architecture',
    title: 'Architecture',
    icon: 'ArchitectureIcon',
    description: 'Define the system architecture',
    status: 'pending',
    step: 11,
    sectionName: 'Technical'
  },
  {
    id: 'dev-guidelines',
    title: 'Development Guidelines',
    icon: 'SettingsIcon',
    description: 'Set coding standards and development practices',
    status: 'pending',
    step: 3,
    sectionName: 'Technical'
  },
  
  // Design tasks
  {
    id: 'wireframes',
    title: 'Wireframes',
    icon: 'PaletteIcon',
    description: 'Create wireframes for your project UI',
    status: 'pending',
    step: 5,
    sectionName: 'Design'
  },
  {
    id: 'design-guidelines',
    title: 'Design Guidelines',
    icon: 'DesignServicesIcon',
    description: 'Define UI/UX design principles and standards',
    status: 'pending',
    step: 4,
    sectionName: 'Design'
  },
  
  // Security and API
  {
    id: 'security-guidelines',
    title: 'Security Guidelines',
    icon: 'SecurityIcon',
    description: 'Define security requirements and practices',
    status: 'pending',
    step: 7,
    sectionName: 'Security & API'
  },
  {
    id: 'api-guidelines',
    title: 'API Guidelines',
    icon: 'ApiIcon',
    description: 'Set standards for API design and implementation',
    status: 'pending',
    step: 8,
    sectionName: 'Security & API'
  },
  
  // User management
  {
    id: 'features',
    title: 'Features',
    icon: 'FeaturedPlayListIcon',
    description: 'Define core features and functionality',
    status: 'pending',
    step: 9,
    sectionName: 'Features'
  },
  {
    id: 'user-stories',
    title: 'User Stories',
    icon: 'PersonIcon',
    description: 'Create user stories for each feature',
    status: 'pending',
    step: 10,
    sectionName: 'Features'
  },
  {
    id: 'user-roles',
    title: 'User Roles',
    icon: 'GroupsIcon',
    description: 'Define user roles and permissions',
    status: 'pending',
    step: 11,
    sectionName: 'Features'
  },
  
  // Flows
  {
    id: 'process-flows',
    title: 'Process Flows',
    icon: 'AccountTreeIcon',
    description: 'Generate sequence diagrams and process flows',
    status: 'pending',
    step: 11,
    sectionName: 'Flows'
  }
];

/**
 * Custom hook to process and manage section tasks
 * 
 * @param {Object} params - Parameters for the hook
 * @param {Object} params.project - The project data
 * @param {Array} params.sections - Available sections
 * @param {boolean} params.isLoadingSections - Flag indicating if sections are loading
 * @param {Object} params.user - Current user information
 * @returns {Object} Section tasks hook results
 */
const useSectionTasks = ({ project, sections, isLoadingSections, user }) => {

  console.log('useSectionTasks', project, sections);
  // State to track conversations for tasks
  const [conversations, setConversations] = useState([]);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  
  // Load conversations for the project
  useEffect(() => {

    const fetchConversations = async () => {
      if (project?.id) {
        try {
          setIsLoadingConversations(true);
          
          const conversationData = await conversationService.getConversations(project.id);
          
          setConversations(Array.isArray(conversationData) ? conversationData : []);
        } catch (error) {
          setConversations([]);
        } finally {
          setIsLoadingConversations(false);
        }
      }
    };
    
    fetchConversations();
  }, [project?.id]);
  // Check if user has access to a section based on role
  const hasAccessToSection = (section) => {
    // Admin has access to all sections
    if (user?.systemRole === 'admin') return true;
    
    // Check if section has required roles and if user's role is included
    if (section.requiredRoles && section.requiredRoles.length > 0) {
      return section.requiredRoles.includes(user?.systemRole || 'user');
    }
    
    // If no required roles specified, default to accessible
    return true;
  };

  // Create fallback foundation tiles data using project data
  const fallbackTiles = useMemo(() => {
    // Update tile status based on project data if available
    return FALLBACK_TILES_DATA.map(tile => {
      // Create a new object to avoid mutating the original data
      const newTile = { ...tile };
      
      // Only update status if project is available and it has completedTasks
      if (project && project.completedTasks && Array.isArray(project.completedTasks)) {
        // Check if the task is in the completedTasks array
        if (project.completedTasks.includes(tile.id)) {
          newTile.status = 'completed';
        } else {
          newTile.status = 'pending';
        }
      }
      
      return newTile;
    });
  }, [project]);

  // Process all section tasks
  const sectionTasks = useMemo(() => {
    // Enhanced debugging logs
    console.log('=== SECTION TASKS DEBUG ===');
    console.log('Project tasks from API:', project?.tasks);
    console.log('Available sections:', sections.map(s => ({
      id: s._id,
      name: s.name,
      taskCount: s.tasks?.length || 0,
      taskIds: s.tasks?.map(t => t.id) || [],
      taskObjectIds: s.tasks?.map(t => t._id) || [],
      taskCustomIds: s.tasks?.map(t => t.task_id) || []
    })));
    
    // Log all section task IDs for comparison
    const allSectionTaskIds = [];
    const allSectionObjectIds = [];
    const allSectionCustomIds = [];
    sections.forEach(section => {
      if (section.tasks) {
        allSectionTaskIds.push(...section.tasks.map(t => t.id));
        allSectionObjectIds.push(...section.tasks.map(t => t._id).filter(Boolean));
        allSectionCustomIds.push(...section.tasks.map(t => t.task_id).filter(Boolean));
      }
    });
    console.log('All section task IDs (custom):', allSectionTaskIds);
    console.log('All section object IDs (_id):', allSectionObjectIds);
    console.log('All section custom IDs (task_id):', allSectionCustomIds);
    console.log('Project task IDs:', project?.tasks);
    
    // Check matches across all ID types
    const taskIdMatches = project?.tasks?.filter(id => allSectionTaskIds.includes(id)) || [];
    const objectIdMatches = project?.tasks?.filter(id => allSectionObjectIds.includes(id)) || [];
    const customIdMatches = project?.tasks?.filter(id => allSectionCustomIds.includes(id)) || [];
    
    console.log('Task ID matches (custom):', taskIdMatches);
    console.log('Object ID matches (_id):', objectIdMatches);
    console.log('Custom ID matches (task_id):', customIdMatches);
    console.log('Total matches:', [...new Set([...taskIdMatches, ...objectIdMatches, ...customIdMatches])]);
    console.log('Missing task IDs:', project?.tasks?.filter(id => 
      !allSectionTaskIds.includes(id) && 
      !allSectionObjectIds.includes(id) && 
      !allSectionCustomIds.includes(id)
    ));
    
    if (isLoadingSections || !sections || sections.length === 0) {
      console.log('Sections loading or empty, returning empty array');
      return [];
    }
    
    // Start with the project tasks array - these are the tasks we want to show
    // Ensure we have the latest tasks from the most up-to-date project source
    const projectTasks = project?.tasks || [];
    console.log('Project tasks to display:', projectTasks);
    
    // Always include these base tasks
    const alwaysIncludeTasks = ['chat-prd'];
    
    // Flatten all tasks from all sections into a single array
    const allTasks = [];
    
    // Add tasks from each section that match the project tasks
    sections.forEach(section => {
      // Skip sections the user doesn't have access to
      if (!hasAccessToSection(section)) return;
      
      if (section.tasks && Array.isArray(section.tasks) && section.tasks.length > 0) {
        // Get all tasks from this section that are included in the project tasks
        // Check _id first since that's what the backend provides
        const sectionTasks = section.tasks.filter(task => {
          // Check if task should always be included
          const isAlwaysIncluded = alwaysIncludeTasks.includes(task.id) || 
                                   alwaysIncludeTasks.includes(task._id);
          
          // Check if task is in project tasks - prioritize _id field
          const isInProjectTasks = projectTasks.includes(task._id) ||
                                   projectTasks.includes(task.id) || 
                                   (task.task_id && projectTasks.includes(task.task_id)) ||
                                   (task.taskId && projectTasks.includes(task.taskId));
          return isAlwaysIncluded || isInProjectTasks;
        });
        
        console.log(`Section "${section.name}" task filtering:`, {
          totalTasks: section.tasks.length,
          filteredTasks: sectionTasks.length,
          allTaskData: section.tasks.map(t => ({ id: t.id, _id: t._id, task_id: t.task_id, title: t.title })),
          filteredIds: sectionTasks.map(t => t.id),
          projectTasksIncluded: sectionTasks.map(t => ({ id: t.id, _id: t._id, task_id: t.task_id, title: t.title }))
        });
        
        // Default colors based on section name as a guaranteed fallback
        const getDefaultColorsBySectionName = (sectionName) => {
          const nameMap = {
            'functional analysis': { bg: '#e8f5e9', icon: '#66bb6a' },
            'design': { bg: '#e3f2fd', icon: '#42a5f5' },
            'coding & development': { bg: '#fff3e0', icon: '#ff9800' },
            'testing': { bg: '#f3e5f5', icon: '#ab47bc' },
            'infrastructure': { bg: '#e0f7fa', icon: '#26c6da' }
          };
          
          // Check if we have a match (case insensitive)
          const normalizedName = sectionName?.toLowerCase();
          return nameMap[normalizedName] || { bg: '#f5f5f5', icon: '#475969' };
        };
        
        // Add section information to each task
        const tasksWithSection = sectionTasks.map(task => {
          // Debug: Log the complete original task object structure
          console.log(`useSectionTasks: Original task object for "${task.title}":`, task);
          console.log(`useSectionTasks: Task keys:`, Object.keys(task));
          console.log(`useSectionTasks: Task ID fields:`, {
            id: task.id,
            _id: task._id,
            task_id: task.task_id,
            taskId: task.taskId
          });
          
          // Log each task's data including its colors
          console.log(`Processing task ${task.id || task._id || task.task_id} (${task.title}):`, {
            taskBgColor: task.backgroundColor,
            taskIconColor: task.iconColor,
            sectionBgColor: section.backgroundColor,
            sectionIconColor: section.iconColor
          });
          
          // Get default colors based on section name as fallback
          const defaultColors = getDefaultColorsBySectionName(section.name);
          
          // Choose the best available colors, with priority:
          // 1. Task colors
          // 2. Section colors
          // 3. Default colors based on section name
          const backgroundColor = task.backgroundColor || section.backgroundColor || defaultColors.bg;
          const iconColor = task.iconColor || section.iconColor || defaultColors.icon;
          
          console.log(`Final colors for ${task.id}: ${backgroundColor}, ${iconColor}`);
          
          // Ensure the task has a proper id field for navigation
          // Prioritize custom string ID (task.id / task.task_id) over MongoDB ObjectId (_id)
          // so permission checks and conversation matching work correctly
          const taskId = task.id || task.task_id || task.taskId || task._id;
          
          // Skip tasks without any ID (safety check)
          if (!taskId) {
            console.warn(`useSectionTasks: Skipping task "${task.title}" - no ID field found`, task);
            return null;
          }
          
          const processedTask = {
            ...task,
            id: taskId, // Ensure id field is always set
            _id: task._id || taskId, // Preserve _id field if it exists
            sectionName: normalizeSectionName(section.name) || section.name,
            sectionId: section._id,
            // Include the best available colors
            backgroundColor,
            iconColor,
            // Also keep sectionColors for reference
            sectionColors: {
              backgroundColor,
              iconColor
            }
          };
          
          console.log(`useSectionTasks: Processed task with ID:`, {
            originalTaskId: task.id,
            computedTaskId: taskId,
            finalTaskId: processedTask.id,
            title: processedTask.title
          });
          
          return processedTask;
        }).filter(Boolean); // Remove any null values from tasks without IDs
        
        allTasks.push(...tasksWithSection);
      }
    });
    
    // Check if any project tasks aren't found in the sections
    const foundTaskIds = allTasks.map(task => task.id);
    const foundObjectIds = allTasks.map(task => task._id).filter(Boolean);
    const foundCustomIds = allTasks.map(task => task.task_id).filter(Boolean);
    
    const missingTasks = projectTasks.filter(taskId => 
      !foundTaskIds.includes(taskId) && 
      !foundObjectIds.includes(taskId) && 
      !foundCustomIds.includes(taskId)
    );
    
    if (missingTasks.length > 0) {
      console.warn('Some project tasks not found in sections:', missingTasks);
      
      // Create fallback tasks for missing tasks
      missingTasks.forEach(taskId => {
        // Try to find a matching fallback tile
        const fallbackTile = FALLBACK_TILES_DATA.find(tile => tile.id === taskId);
        
        if (fallbackTile) {
          allTasks.push({
            id: taskId,
            title: fallbackTile.title || taskId,
            icon: fallbackTile.icon,
            description: fallbackTile.description || '',
            sectionName: 'Other Tasks',
            status: 'pending'
          });
        } else {
          // Create a completely generic task if no fallback is found
          allTasks.push({
            id: taskId,
            title: taskId.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
            icon: 'FolderIcon',
            description: '',
            sectionName: 'Other Tasks',
            status: 'pending'
          });
        }
      });
    }
    
    // Hide knowledge-hub tasks from all sections
    const visibleTasks = allTasks.filter(task => !task.id?.startsWith('knowledge-hub'));

    console.log('Final tasks to display:', visibleTasks);
    return visibleTasks;
  }, [sections, isLoadingSections, project, hasAccessToSection]);

  // Generate filtered and processed tiles
  const filteredTiles = useMemo(() => {
    // If backend data is loading, use the original fallbackTiles as fallback
    if (isLoadingSections || !sections || sections.length === 0) {
      // If no project data is available yet, show standard tasks
      if (!project || !project.tasks || !Array.isArray(project.tasks)) {
        // Show default tiles if no project tasks available
        console.log('Using default tasks - no project tasks available');
        return fallbackTiles.filter(tile => 
          tile.id === 'chat-prd' || tile.id === 'extract-prd'
        );
      }
      
      // Use project tasks directly even if sections aren't loaded yet
      console.log('Using fallback tiles with project tasks', project.tasks);
      const projectTasksSet = new Set(project.tasks);
      
      // Start with always-included tasks
      const alwaysIncluded = fallbackTiles.filter(tile => 
        tile.id === 'chat-prd' || tile.id === 'extract-prd'
      );
      
      // Add project-specific tasks that exist in fallbackTiles
      const projectSpecificTasks = fallbackTiles.filter(tile => 
        tile.id !== 'chat-prd' && tile.id !== 'extract-prd' && projectTasksSet.has(tile.id)
      );
      
      // Add unknown tasks with generic formatting
      const unknownTasks = project.tasks
        .filter(taskId => 
          taskId !== 'chat-prd' && 
          taskId !== 'extract-prd' && 
          !fallbackTiles.some(tile => tile.id === taskId)
        )
        .map(taskId => ({
          id: taskId,
          title: taskId.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
          icon: 'FolderIcon',
          description: '',
          status: 'pending',
          step: 0,
          sectionName: 'Other Tasks'
        }));
      
      // Convert string icon names to components before returning
      return [...alwaysIncluded, ...projectSpecificTasks, ...unknownTasks].map(tile => ({
        ...tile,
        icon: getIconComponent(tile.icon)
      }));
    }
    
    // If section data is available, use it with project tasks
    if (sectionTasks.length > 0) {
      console.log('Using section tasks with project tasks', sectionTasks.length);
      
      // Convert backend tasks to the format expected by the component
      return sectionTasks.map(task => {
        // Determine task status based on completion and conversations
        const taskStatus = getTaskStatus(
          task, 
          project?.completedTasks || [], 
          conversations
        );
        
        return {
          id: task.id,
          title: task.title || task.id.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
          icon: getIconComponent(task.icon),
          description: task.description || '',
          // Use our enhanced status instead of just 'completed' or 'pending'
          status: taskStatus,
          step: task.order || 0,
          sectionName: task.sectionName || 'General',
          sectionId: task.sectionId,
          // Preserve section colors that come from the backend
          sectionColors: task.sectionColors
        };
      });
    }
    
    // Fallback if nothing else works
    console.log('Using fallback with project tasks', project?.tasks);
    return fallbackTiles
      .filter(tile => 
        tile.id === 'chat-prd' || 
        tile.id === 'extract-prd' || 
        (project?.tasks && project.tasks.includes(tile.id))
      )
      .map(tile => ({
        ...tile,
        icon: getIconComponent(tile.icon)
      }));
  }, [fallbackTiles, project, isLoadingSections, sections, sectionTasks]);

  // Group tiles by section
  const groupedTiles = useMemo(() => {
    const groups = {};
    
    filteredTiles.forEach(tile => {
      const sectionName = tile.sectionName || 'General';
      if (!groups[sectionName]) {
        groups[sectionName] = [];
      }
      groups[sectionName].push(tile);
    });
    
    return groups;
  }, [filteredTiles]);

  return {
    filteredTiles,
    groupedTiles,
    sectionTasks,
    fallbackTiles,
    conversations,
    isLoadingConversations
  };
};

export default useSectionTasks;